import { useEffect, useSyncExternalStore } from "react";
import {
  applyFleetMessage,
  emptyFleetState,
  fleetStreamUrl,
  parseFleetMessage,
  type FleetMessage,
  type FleetState,
} from "./fleet-stream";

type StateListener = () => void;
type MessageListener = (message: FleetMessage) => void;

interface FleetConnection {
  state: FleetState;
  refs: number;
  retry: number;
  ws: WebSocket | null;
  reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  pingTimer: ReturnType<typeof setInterval> | undefined;
  stateListeners: Set<StateListener>;
  messageListeners: Set<MessageListener>;
}

const connections = new Map<string, FleetConnection>();

function createConnection(): FleetConnection {
  return {
    state: emptyFleetState(),
    refs: 0,
    retry: 0,
    ws: null,
    reconnectTimer: undefined,
    pingTimer: undefined,
    stateListeners: new Set(),
    messageListeners: new Set(),
  };
}

function getConnection(apiBaseUrl: string): FleetConnection {
  const url = fleetStreamUrl(apiBaseUrl);
  const existing = connections.get(url);
  if (existing) return existing;
  const next = createConnection();
  connections.set(url, next);
  return next;
}

function notifyState(connection: FleetConnection): void {
  for (const listener of connection.stateListeners) listener();
}

function clearConnectionTimers(connection: FleetConnection): void {
  if (connection.reconnectTimer) clearTimeout(connection.reconnectTimer);
  if (connection.pingTimer) clearInterval(connection.pingTimer);
  connection.reconnectTimer = undefined;
  connection.pingTimer = undefined;
}

function connect(apiBaseUrl: string, connection: FleetConnection): void {
  const url = fleetStreamUrl(apiBaseUrl);

  const scheduleReconnect = () => {
    connection.retry += 1;
    const delay = Math.min(30_000, 1_000 * 2 ** Math.min(connection.retry, 5));
    connection.reconnectTimer = setTimeout(() => connect(apiBaseUrl, connection), delay);
  };

  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  connection.ws = ws;

  ws.onopen = () => {
    connection.retry = 0;
    connection.pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 25_000);
  };
  ws.onmessage = (ev) => {
    if (typeof ev.data !== "string" || ev.data === "pong") return;
    const msg = parseFleetMessage(ev.data);
    if (!msg) return;
    for (const listener of connection.messageListeners) listener(msg);
    connection.state = applyFleetMessage(connection.state, msg);
    notifyState(connection);
  };
  ws.onclose = () => {
    if (connection.pingTimer) clearInterval(connection.pingTimer);
    connection.pingTimer = undefined;
    connection.ws = null;
    if (connection.refs > 0) scheduleReconnect();
  };
  ws.onerror = () => {
    ws.close();
  };
}

function acquireConnection(apiBaseUrl: string): () => void {
  const url = fleetStreamUrl(apiBaseUrl);
  const connection = getConnection(apiBaseUrl);
  connection.refs += 1;
  if (!connection.ws && !connection.reconnectTimer) connect(apiBaseUrl, connection);

  return () => {
    connection.refs = Math.max(0, connection.refs - 1);
    if (connection.refs > 0) return;
    clearConnectionTimers(connection);
    connection.ws?.close();
    connection.ws = null;
    connections.delete(url);
  };
}

/**
 * F10 配車リアルタイム配信の購読フック。
 *
 * 同じ URL の購読はプロセス内で共有し、root の invalidate 用購読と /status の表示用購読が
 * 1 画面で二重 WebSocket にならないようにする。
 */
export function useFleetStream(opts: {
  apiBaseUrl: string;
  enabled?: boolean;
  onMessage?: (message: FleetMessage) => void;
}): FleetState {
  const { apiBaseUrl, enabled = true, onMessage } = opts;
  const connection = getConnection(apiBaseUrl);
  const state = useSyncExternalStore(
    (listener) => {
      connection.stateListeners.add(listener);
      return () => {
        connection.stateListeners.delete(listener);
      };
    },
    () => connection.state,
    () => emptyFleetState(),
  );

  useEffect(() => {
    if (!enabled) return undefined;
    return acquireConnection(apiBaseUrl);
  }, [apiBaseUrl, enabled]);

  useEffect(() => {
    if (!enabled || !onMessage) return undefined;
    connection.messageListeners.add(onMessage);
    return () => {
      connection.messageListeners.delete(onMessage);
    };
  }, [connection, enabled, onMessage]);

  return state;
}

export function __resetFleetStreamForTests(): void {
  for (const connection of connections.values()) {
    clearConnectionTimers(connection);
    connection.ws?.close();
  }
  connections.clear();
}
